import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  DatePicker,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { sendInvite, getInvites, deleteInvite, resendInvite } from '../../services/inviteService';
import useGetAdminRoles from '../../hooks/admin-management/useGetAdminRoles';
import dayjs from 'dayjs';

const { Option } = Select;

interface Invite {
  id: string;
  recipient: string;
  role_name: string;
  status: 'pending' | 'accepted';
  created: number;
  role_expiration_time?: number;
  notification_id?: string;
}

interface FetchParams {
  next_cursor?: string;
  status?: string;
  recipient?: string;
  [key: string]: any;
}

const InviteManagement = () => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  const { data: adminRoles, isPending: isLoadingRoles } = useGetAdminRoles({});

  const fetchInvites = async (params: FetchParams = {}, append = false) => {
    if (loading) return; // Prevent multiple simultaneous calls
    
    setLoading(true);
    try {
      const response = await getInvites({
        next_cursor: params.next_cursor,
        limit: 15,
        status: params.status,
        recipient: params.recipient,
        ...params,
      });
      
      if (append) {
        setInvites(prev => [...prev, ...response.invites]);
      } else {
        setInvites(response.invites);
      }
      
      setNextCursor(response.next_cursor || null);
      setHasMore(!!response.next_cursor);
      setInitialLoad(false);
    } catch (error) {
      message.error('Failed to fetch invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []); // Empty dependency array to run only once

  useEffect(() => {
    // Only set up scroll listener after initial load is complete
    if (initialLoad) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      
      // Load more when user scrolls to bottom
      if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading && nextCursor) {
        fetchInvites({ next_cursor: nextCursor }, true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading, nextCursor, initialLoad]);

  const handleRefresh = () => {
    setNextCursor(null);
    setHasMore(true);
    setInitialLoad(true);
    fetchInvites();
  };

  const handleSendInvite = async (values: any) => {
    setLoading(true);
    try {
      const response = await sendInvite({
        email: values.email, // Keep as email since that's what the form field is named
        role_id: values.role,
        expires_at: values.expires_at ? values.expires_at.toISOString() : undefined,
      });
      
      message.success('Invite sent successfully');
      form.resetFields();
      setModalVisible(false);
      handleRefresh(); // Refresh the list to show the new invite
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to send invite";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvite = async (id: string) => {
    try {
      await deleteInvite(id);
      message.success('Invite deleted successfully');
      // Remove the deleted invite from the current list
      setInvites(prev => prev.filter(invite => invite.id !== id));
    } catch (error) {
      message.error('Failed to delete invite');
    }
  };

  const handleResendInvite = async (record: Invite) => {
    if (!record.notification_id) {
      message.error('Cannot resend invite: No notification ID found');
      return;
    }

    try {
      await resendInvite(record.notification_id, true);
      message.success('Invite resent successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to resend invite";
      message.error(errorMessage);
    }
  };

  const columns = [
    {
      title: 'Recipient',
      dataIndex: 'recipient',
      key: 'recipient',
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      key: 'role_name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Invite) => {
        let statusText = status.charAt(0).toUpperCase() + status.slice(1);
        let statusColor = '#52c41a'; // default green for accepted
        
        if (status === 'pending') {
          statusColor = '#faad14'; // orange
        } else if (status === 'accepted') {
          statusColor = '#52c41a'; // green
        } else {
          statusColor = '#ff4d4f'; // red for other cases
        }
        
        return (
          <span style={{ color: statusColor }}>
            {statusText}
          </span>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: (timestamp: number) => dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Expiration',
      dataIndex: 'role_expiration_time',
      key: 'role_expiration_time',
      render: (timestamp?: number) => timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Invite) => (
        <Space>
          {record.status === 'pending' && (
            <>
              <Button 
                type="text" 
                icon={<ReloadOutlined />}
                onClick={() => handleResendInvite(record)}
                title="Resend Invite"
              >
                Resend
              </Button>
              <Popconfirm
                title="Are you sure you want to delete this invite?"
                onConfirm={() => handleDeleteInvite(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                >
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Invite User
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={invites}
        rowKey="id"
        loading={loading}
        pagination={false} // Disable built-in pagination
      />

      {loading && hasMore && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading more invites...
        </div>
      )}

      <Modal
        title="Create User"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSendInvite}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email address!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role!' }]}
          >
            <Select 
              placeholder="Select role"
              loading={isLoadingRoles}
            >
              {adminRoles?.map((role) => (
                <Option key={role.id} value={role.id}>
                  {role.display_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="expires_at"
            label="Role Expiration (Optional)"
          >
            <DatePicker 
              showTime 
              format="YYYY-MM-DD HH:mm:ss"
              placeholder="Select expiration date and time"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create User
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InviteManagement;